/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   3/16/22
 */

import React from "react";
import {IconButton, List, ListItem, ListItemButton, ListItemText, Stack, Typography} from "@mui/material";
import {useQuery} from "urql";
import {SavedCalendarsQuery, SavedCalendarsQueryVars, SavedCalendarsResponse} from "../graphql/queries/calendars";
import HeroMessage from "../components/HeroMessage";
import LoadingHero from "../components/LoadingHero";
import {Calendar} from "../interfaces/Global";
import ClearIcon from '@mui/icons-material/Clear';
import {Link} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {State} from "../state/store";
import {remove} from "../state/reducers/favorites";

export default function FavoriteCalendars() {

	const favoriteCalendars = useSelector((s:State) => s.favorites);

	const [ calendarsResult ] = useQuery<SavedCalendarsResponse, SavedCalendarsQueryVars>({
		query: SavedCalendarsQuery,
		variables: {
			idIn: favoriteCalendars
		},
		pause: favoriteCalendars.length === 0
	});

	const calendars = (favoriteCalendars.length > 0 && calendarsResult.data?.savedCalendars.items) || [];

	return (
		<Stack spacing={1}>
			<Typography variant={"h5"} sx={{ pb: 2 }}>Favorite Calendars</Typography>
			{calendarsResult.error &&
				<HeroMessage
					p={4}
					title={"Request Error"}
					titleTypographyProps={{
						variant: "h6"
					}}
					message={calendarsResult.error.message}
				/>
			}
			{calendarsResult.fetching &&
				<LoadingHero />
			}
			{!calendarsResult.error && !calendarsResult.fetching && calendars.length === 0 &&
				<HeroMessage
					p={4}
					title={"You don't have any favorite calendars"}
					titleTypographyProps={{
						variant: "h6"
					}}
					message={"You can star calendars created by others for quick access"}
				/>
			}
			{!calendarsResult.error && !calendarsResult.fetching && calendars.length > 0 &&
				<List dense disablePadding>
					{calendars.map(calendar =>
						<SavedCalendarItem
							key={calendar.id}
							calendar={calendar}
						/>)}
				</List>}
		</Stack>
	);

}

function SavedCalendarItem ({ calendar } : { calendar: Calendar }) {

	const dispatch = useDispatch();

	return (
		<ListItem
			disableGutters
			disablePadding
			divider
			secondaryAction={
				<IconButton
					size={"small"}
					edge={"end"}
					onClick={() => {
						dispatch(remove(calendar.id));
					}}
				>
					<ClearIcon />
				</IconButton>
			}
		>
			<ListItemButton disableGutters component={Link} to={"/calendar/" + calendar.id}>
				<ListItemText
					primary={calendar.title}
					secondaryTypographyProps={{
						sx: {
							fontSize: "0.8em"
						}
					}}
					secondary={
						(calendar.createdByEmployee ?
							calendar.createdByEmployee?.displayName + " (" + calendar.createdBy + ")" :
							calendar.createdBy)
					}
				/>
			</ListItemButton>
		</ListItem>
	);
}